import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useEditClientSetMasterAccountsMutation, useLazyFetchClientSetMasterAccountsQuery } from "../../../services/campaignmanagement";
import { useClientSetContext } from "./ClientSetContext";

const useClientSetAccountsGrid = () => {
    const {
        selectedSetId,
        searchTerm,
        shouldClear,
    } = useClientSetContext();

    const [fetchAccounts, { data, isLoading, isFetching, isError, error }] = useLazyFetchClientSetMasterAccountsQuery()

    const { results, pagination } = data || { results: [], pagination: { totalCount: 0, page: 1, pageSize: 50, totalPages: null } };
    const { page, pageSize: limit, totalCount } = pagination;

    const [sort, setSort] = useState(null);
    const [sortProperty, direction] = useMemo(() => sort?.split("+") || [], [sort]);

    const [isClearing, setIsClearing] = useState(false);
    const [cacheKey, setCacheKey] = useState(new Date().valueOf());
    const clear = () => {
        return new Promise((res, rej) => {
            if (selectedSetId == null || isClearing)
                return;

            setIsClearing(true);

            const newCacheKey = new Date().valueOf();
            return fetchAccounts({
                cacheKey: newCacheKey,
                clientSetId: selectedSetId,
                // masterAccountIds,
                page: 1,
                limit,
                sort,
                searchTerm
            }).unwrap()
                .then(() => setCacheKey(newCacheKey))
                .then(res, rej)
                .finally(() => setIsClearing(false));
        })
    }

    const isRowLoaded = (index) => (index < results.length);

    const loadMore = () => {
        if (isLoading || isFetching || isError) return;
        return fetchAccounts({
            cacheKey,
            clientSetId: selectedSetId,
            // masterAccountIds,
            page: page + 1,
            limit,
            sort,
            searchTerm
        }).unwrap();
    }

    const onClickSort = (property) => {
        const newSort = property === sortProperty ? `${property}+${direction === "asc" ? "desc" : "asc"}` : `${property}+asc`;
        setSort(newSort);
    }

    useEffect(() => {
        clear();
    }, [selectedSetId, sort]);

    useEffect(() => {
        if (shouldClear)
            clear();
    }, [shouldClear]);

    useEffect(() => {
        const searchTimeout = setTimeout(() => {
            if (searchTerm !== null) {
                clear();
            }
        }, 150);

        return () => clearTimeout(searchTimeout);
    }, [searchTerm]);

    const [editAccountsTrigger, { isLoading: isEditing }] = useEditClientSetMasterAccountsMutation();

    const editAccounts = ({ masterAccountId, add = false }) => {
        return toast.promise(editAccountsTrigger({
            clientSetId: selectedSetId,
            masterAccountId,
            add
        }).unwrap(), {
            loading: "Updating client set...",
            success: () => {
                clear();
                return "Client set updated successfully!";
            },
            error: (err) => {
                console.error(err);
                return err?.data?.message ?? err?.data ?? "Failed to update client set.";
            }
        });
    }

    return {
        results: selectedSetId == null ? [] : results,
        totalCount: selectedSetId == null ? 0 : totalCount,
        sort,
        onClickSort,
        isClearing,
        isError,
        error,
        clear,
        loadMore,
        isRowLoaded,
        editAccounts,
        isEditing
    }
}

export default useClientSetAccountsGrid;